/**
 * Permission utility functions for role-based access control
 */

/**
 * Get the current user's active role
 * @returns {string|null} The active role name or null
 */
export const getActiveRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.roles) {
      return null;
    }

    const activeRole = user.roles.find((r) => r.active === true);
    return activeRole ? activeRole.role : null;
  } catch (ex) {
    console.error('Error getting active role:', ex);
    return null;
  }
};

export const getActiveRoleData = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    if (!user || !user.roles) return null;
    return user.roles.find((r) => r.active === true) || null;
  } catch (ex) {
    return null;
  }
};

/**
 * Get the organization the user is currently working in. Falls back to the
 * first membership when nothing is flagged active — a user who has never picked
 * an organization still has to land somewhere.
 * @returns {Object|null} The active organization, or null
 */
export const getActiveOrganization = () => {
  try {
    const organizations = JSON.parse(localStorage.getItem('user'))?.organizations;
    if (!Array.isArray(organizations)) return null;
    return (
      organizations.find((org) => org.active === true) || organizations[0] || null
    );
  } catch (ex) {
    console.error('Error getting active organization:', ex);
    return null;
  }
};

/**
 * Get the id of the organization the user is currently working in.
 * @returns {string|null} The active organization id, or null
 */
export const getActiveOrganizationId = () => getActiveOrganization()?.id || null;

/**
 * Get the care provider record the user acts as in the active organization.
 * @returns {Object|null} The active care provider, or null
 */
export const getActiveCareProvider = () => getActiveOrganization()?.careProvider || null;

/**
 * Get the clinical location the user is currently working in.
 *
 * The role and organization `active` flags are set by two separate endpoints
 * (`/user/select-role` and `/user/select-organization`), and selecting an
 * organization clears every role flag — so the active role can be left pointing
 * at a location in an organization the user is no longer working in. Anchoring
 * on the active organization first keeps the two in step.
 *
 * @returns {string|null} The active clinical location id, or null
 */
export const getActiveClinicalLocationId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    const roles = user?.roles;
    if (!Array.isArray(roles)) return null;

    const locationRoles = roles.filter(
      (r) => r.type === 'CLINICAL_LOCATION' && r.clinicalLocationId
    );
    // Strictly the organization flagged active — getActiveOrganization's
    // fall-back-to-first would just reintroduce the mismatch this guards against.
    const activeOrganizationId = user?.organizations?.find(
      (org) => org.active === true
    )?.id;
    const inActiveOrganization = activeOrganizationId
      ? locationRoles.filter((r) => r.organizationId === activeOrganizationId)
      : [];

    // Best to worst: the active role in the active organization, any role in the
    // active organization, the active role wherever it is, any location role.
    const role =
      inActiveOrganization.find((r) => r.active === true) ||
      inActiveOrganization[0] ||
      locationRoles.find((r) => r.active === true) ||
      locationRoles[0];

    return role?.clinicalLocationId || null;
  } catch (ex) {
    console.error('Error getting active clinical location:', ex);
    return null;
  }
};

/**
 * Get the current user's permissions
 * @returns {Array<string>} Array of permission strings
 */
export const getUserPermissions = () => {
  try {
    const activeRole = getActiveRole();

    // CLINICAL_LOCATION_ADMIN has full access to all permissions
    if (activeRole === 'CLINICAL_LOCATION_ADMIN') {
      return Object.values(PERMISSIONS);
    }

    if (activeRole !== 'HEALTH_CARE_PROVIDER') {
      return [];
    }

    // Prefer patient-specific permissions fetched from API
    const patientPermissions = localStorage.getItem('patientPermissions');
    if (patientPermissions) {
      return JSON.parse(patientPermissions);
    }

    // Fall back to user-level permissions
    const user = JSON.parse(localStorage.getItem('user'));
    if (user && user.permissions) {
      return user.permissions;
    }

    return [];
  } catch (ex) {
    console.error('Error getting user permissions:', ex);
    return [];
  }
};

/**
 * Check if the user has a specific permission
 * @param {string} permission - The permission to check
 * @returns {boolean} True if the user has the permission
 */
export const hasPermission = (permission) => {
  const permissions = getUserPermissions();
  return permissions.includes(permission);
};

/**
 * Check if the user has any of the specified permissions
 * @param {Array<string>} permissionList - Array of permissions to check
 * @returns {boolean} True if the user has at least one permission
 */
export const hasAnyPermission = (permissionList) => {
  const permissions = getUserPermissions();
  return permissionList.some((permission) => permissions.includes(permission));
};

/**
 * Check if the user has all of the specified permissions
 * @param {Array<string>} permissionList - Array of permissions to check
 * @returns {boolean} True if the user has all permissions
 */
export const hasAllPermissions = (permissionList) => {
  const permissions = getUserPermissions();
  return permissionList.every((permission) => permissions.includes(permission));
};

/**
 * Permission constants for care plan operations
 */
export const PERMISSIONS = {
  // Care Plan permissions
  CREATE_CAREPLAN: 'CREATE_CAREPLAN',
  READ_CAREPLAN: 'READ_CAREPLAN',
  UPDATE_CAREPLAN: 'UPDATE_CAREPLAN',
  DELETE_CAREPLAN: 'DELETE_CAREPLAN',

  // Functional Score permissions
  CREATE_FUNCTIONAL_SCORE: 'CREATE_FUNCTIONAL_SCORE',
  READ_FUNCTIONAL_SCORE: 'READ_FUNCTIONAL_SCORE',
  UPDATE_FUNCTIONAL_SCORE: 'UPDATE_FUNCTIONAL_SCORE',
  DELETE_FUNCTIONAL_SCORE: 'DELETE_FUNCTIONAL_SCORE',

  // Functional Checklist permissions
  CREATE_FUNCTIONAL_CHECKLIST: 'CREATE_FUNCTIONAL_CHECKLIST',
  READ_FUNCTIONAL_CHECKLIST: 'READ_FUNCTIONAL_CHECKLIST',
  UPDATE_FUNCTIONAL_CHECKLIST: 'UPDATE_FUNCTIONAL_CHECKLIST',
  DELETE_FUNCTIONAL_CHECKLIST: 'DELETE_FUNCTIONAL_CHECKLIST',
};

/**
 * Get user information including role and permissions
 * @returns {Object} User information object
 */
export const getUserInfo = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user'));
    const activeRole = getActiveRole();
    const permissions = getUserPermissions();

    return {
      user,
      activeRole,
      permissions,
      isHealthCareProvider: activeRole === 'HEALTH_CARE_PROVIDER',
    };
  } catch (ex) {
    console.error('Error getting user info:', ex);
    return {
      user: null,
      activeRole: null,
      permissions: [],
      isHealthCareProvider: false,
    };
  }
};
